import React, { useState } from "react";
import {
  CircularProgress,
  DialogContent,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import { useRouteMatch } from "react-router-dom";

import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { publishThirdParty } from "../../../../services/menuApp/thirdPartyPublishService";

const useStyles = makeStyles<CustomTheme>((theme) =>
  createStyles({
    responseBox: {
      marginTop: "12px",
      maxHeight: "320px",
      overflowY: "auto",
      borderRadius: "10px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      backgroundColor: theme.palette.background.entity_background,
      padding: "12px",
    },
    buttonRow: {
      marginTop: "24px",
      display: "flex",
      justifyContent: "flex-end",
    },
  }),
);

export interface PublishModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * PublishModal Component
 *
 * Asks for a confirmation before the products are published to the 3rd party.
 * Confirming sends the publish request with no body, a loading indicator is
 * shown until the response comes back, and the response data is then shown
 * inside the modal.
 */
const PublishModal: React.FunctionComponent<PublishModalProps> = ({
  open,
  setOpen,
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [responseData, setResponseData] = useState<any>(null);
  const [isResponseReceived, setIsResponseReceived] = useState(false);

  const theme: CustomTheme = useTheme();
  const classes = useStyles();
  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;

  /* Everything is reset on close, so the next time the modal is opened it
     starts from the confirmation message again. */
  const handleClose = () => {
    if (isPublishing) return;
    setOpen(false);
    setResponseData(null);
    setIsResponseReceived(false);
  };

  /* Sends the publish request. No body is sent with it. */
  const handleConfirm = async () => {
    setIsPublishing(true);
    try {
      const response = await publishThirdParty(locationId);
      setResponseData(response?.data);
    } catch (err) {
      const axiosError: any = err;
      setResponseData(
        axiosError?.response?.data || {
          message: ERROR_MESSAGE_UNEXPECTED_ERROR,
        },
      );
    } finally {
      setIsResponseReceived(true);
      setIsPublishing(false);
    }
  };

  /* The backend can answer with http 200 and still carry an error inside the
     body, so the body status decides whether the publish worked. */
  const bodyStatus = responseData?.status;
  const isPublished =
    !!responseData?.data && (bodyStatus === 200 || bodyStatus === 201);

  /* Shows the account id and the name of the published catalog, or the error
     message when the publish did not work. */
  const renderResponse = () => {
    if (!isPublished) {
      return (
        <Typography
          variant="body1"
          align="left"
          style={{ color: theme.palette.error.main }}
        >
          {responseData?.message || ERROR_MESSAGE_UNEXPECTED_ERROR}
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="body2" align="left" style={{ opacity: 0.7 }}>
          Account ID
        </Typography>
        <Typography
          variant="body1"
          align="left"
          style={{ fontWeight: "bold", wordBreak: "break-word" }}
        >
          {responseData.data.account_id || "-"}
        </Typography>
        <Typography
          variant="body2"
          align="left"
          style={{ opacity: 0.7, marginTop: "12px" }}
        >
          Name
        </Typography>
        <Typography
          variant="body1"
          align="left"
          style={{ fontWeight: "bold", wordBreak: "break-word" }}
        >
          {responseData.data.name || "-"}
        </Typography>
      </>
    );
  };

  return (
    <DialogCommonDefault
      open={open}
      setOpen={handleClose}
      isNeedFixedHeight={false}
      maxWidth="sm"
    >
      <DialogContent style={{ padding: "24px" }}>
        <Typography variant="h6" align="left" style={{ fontWeight: "bold" }}>
          Publish
        </Typography>

        {isPublishing ? (
          <div
            style={{
              marginTop: "32px",
              marginBottom: "32px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress color="secondary" disableShrink />
            <Typography variant="body2" style={{ marginTop: "16px" }}>
              Publishing, please wait...
            </Typography>
          </div>
        ) : isResponseReceived ? (
          <div className={classes.responseBox}>{renderResponse()}</div>
        ) : (
          <Typography
            variant="body1"
            align="left"
            style={{ marginTop: "16px", color: theme.palette.text.primary }}
          >
            Are you sure you want to publish the selected products to the 3rd
            party?
          </Typography>
        )}

        {!isPublishing && (
          <div className={classes.buttonRow}>
            {isResponseReceived ? (
              <ButtonCommon
                variant="contained"
                color="yellow"
                style={{ minWidth: "100px" }}
                onClick={handleClose}
              >
                Close
              </ButtonCommon>
            ) : (
              <>
                <ButtonCommon
                  variant="contained"
                  color="red"
                  style={{ minWidth: "100px", marginRight: "12px" }}
                  onClick={handleClose}
                >
                  Cancel
                </ButtonCommon>
                <ButtonCommon
                  variant="contained"
                  color="blue"
                  style={{ minWidth: "100px" }}
                  onClick={handleConfirm}
                >
                  Confirm
                </ButtonCommon>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </DialogCommonDefault>
  );
};

export default PublishModal;